import React, { useContext, useEffect, useState } from "react";

import {
  Badge,
  Box,
  Button,
  Card,
  Cell,
  Divider,
  Dropdown,
  IconButton,
  Layout,
  Loader,
  MessageModalLayout,
  Modal,
  Page,
  Pagination,
  Search,
  Table,
  TableToolbar,
  Tabs,
  Text,
  Tooltip,
  WixDesignSystemProvider,
  accordionItemBuilder,
  AutoComplete,
} from "@wix/design-system";
import "@wix/design-system/styles.global.css";
import * as Icons from "@wix/wix-ui-icons-common";
import FaireProduct from "../../../models/faire-product";
import { showToast } from "@wix/dashboard-sdk";

import { withProviders } from "../../withProviders";
import WixLogo from "../wix_logo.svg";
import FiareLogo from "../fiare_logo.svg";
import "../index.css";
import SiteContext from "../../../contexts/site-context";
import { addExpandProperties, mergeArrays, truncateText } from "../../../services/utils/productUtils";
import { useDebouncedCallback } from "use-debounce";
import Loading from "../../../components/common/Loader";
import picture from "../../images/no-image.png";
import ExportModal from "../../../components/common/modal/ExportModal";
import { useTableItemSelection } from "../../../hooks/useTableItemSelection";
import useCategoryFetching, {
  useFaireProductBulkExport,
  useFaireProductImport,
  useFaireProducts,
  useFaireProductsCount,
} from "../../../hooks/faire/useFaire";
import EmptyTable from "../../../components/common/Table/EmptyTable";
import { faireStatusOptions } from "../../../config/constants/products/faire.constant";
import SidePanelModal from "../../../components/common/modal/SidePanelModal";
import TrialExpiryAlert from "../../../components/trial-expiry-alert";
import {
  usePusherBulkExportProgress,
  usePusherExportProgress,
  usePusherImportProgress,
} from "../../../hooks/usePusher";
import { useWixProductsDelete } from "../../../hooks/wix/useWix";
import WixConfig from "../../../config/wix";
import SyncedDate from "../../../components/common/SyncedDate";

const Index = () => {
  // Get Site Details
  const { siteId } = useContext(SiteContext);

  /*
  |--------------------------------------------------------------------------
  | Upload to Wix
  |--------------------------------------------------------------------------
  |
  */
  const [showConirmationForUploadToWix, setShowConfirmationForUploadToWix] = React.useState<boolean>(false);
  const [selectedProductId, setSelectedProductId] = React.useState<string>("");
  const [uploadingToWix, setUploadingToWix] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (selectedProductId !== "") {
      setShowConfirmationForUploadToWix(true);
    }
  }, [selectedProductId]);

  /*
  |--------------------------------------------------------------------------
  | Upload To wix
  |--------------------------------------------------------------------------
  |
  */
  const uploadToWix = () => {
    if (uploadingToWix) return;
    setUploadingToWix(true);
    FaireProduct.uploadToWix(selectedProductId)
      .then((response) => {
        showToast({
          type: "success",
          message: response.message,
        });
      })
      .catch((error) => {
        showToast({
          type: "error",
          message: error.message,
        });
      })
      .finally(() => {
        faireProductsQuery.refetch();
        setUploadingToWix(false);
        setSelectedProductId("");
        setShowConfirmationForUploadToWix(false);
      });
  };

  /*
  |--------------------------------------------------------------------------
  | Upload to wix cancellation
  |--------------------------------------------------------------------------
  |
  */
  const handleCancelUploadToWix = () => {
    setSelectedProductId("");
  };

  /*
  |--------------------------------------------------------------------------
  | Upload To Wix Confirmation
  |--------------------------------------------------------------------------
  |
  */
  const handleConfirmUploadToWix = () => {
    uploadToWix();
  };

  /*
  |--------------------------------------------------------------------------
  | Flag: should show confirmation about whether to import faire products or
  | not.
  |--------------------------------------------------------------------------
  |
  */
  const [showConfirmationForImport, setShowConfirmationForImport] = useState(false);

  /*
  |--------------------------------------------------------------------------
  | Category Fetch
  |--------------------------------------------------------------------------
  |
  */
  const {
    setKeywords: setCategoryKeywords,
    fetchCategories,
    keywords: categoryKeywords,
    hasMore,
    faireCategoryQuery,
    faireCategoryOptions,
    debouncedCategory,
  } = useCategoryFetching();

  /*
  |--------------------------------------------------------------------------
  | Pagination details
  |--------------------------------------------------------------------------
  |
  */
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const itemsPerPage = 10;

  /*
  |--------------------------------------------------------------------------
  | Filter parameters
  |--------------------------------------------------------------------------
  |
  */
  const [keywords, setKeywords] = useState("");
  const [status, setStatus] = useState<string | number>(" ");
  const [categoryId, setCategoryId] = useState<string | number>(" ");
  const [exportStatus, setExportStatus] = React.useState<string | number>(" ");

  const selectedCategory = faireCategoryOptions.find((item) => item.id === categoryId);

  /*
  |--------------------------------------------------------------------------
  | Manage Products Count
  |--------------------------------------------------------------------------
  |
  */
  const {
    data: ProductCount,
    isSuccess,
    isFetching: isCurrentlyFetching,
    refetch: faireProductCountRefetch,
  } = useFaireProductsCount();
  const countData = isSuccess && !isCurrentlyFetching ? ProductCount?.data : null;

  const [exportedData, setExportedData] = useState<number | null>(null);
  const [notExportedData, setNotExportedData] = useState<number | null>(null);

  useEffect(() => {
    if (isSuccess && !isCurrentlyFetching && ProductCount?.data) {
      const { exported_products, not_exported_products } = ProductCount?.data;
      setExportedData(exported_products);
      setNotExportedData(not_exported_products);
    }
  }, [isSuccess, isCurrentlyFetching, ProductCount]);

  /*
  |--------------------------------------------------------------------------
  | Count Display Conditions
  |--------------------------------------------------------------------------
  |
  */
  const allProductCountCondition = countData?.all_products && countData?.all_products !== 0;
  const importedProductCountCondition = countData?.imported_products && countData?.imported_products !== 0;

  /*
  |--------------------------------------------------------------------------
  | Export Status Options
  |--------------------------------------------------------------------------
  |
  */
  const exportStatusOptions = [
    {
      id: " ",
      title: (
        <Tooltip maxWidth={360} content="Here is the list of all your products currently in the Faire Store.">
          <Text>
            All Products
            {allProductCountCondition ? ` (${countData?.all_products})` : ""}
          </Text>
        </Tooltip>
      ),
      maxWidth: 158,
    },
    {
      id: "IMPORTED",
      title: (
        <Tooltip
          maxWidth={360}
          content="Here is the list of products that have been uploaded from your store into Faire store"
        >
          <Text>
            Imported
            {importedProductCountCondition ? ` (${countData?.imported_products})` : ""}
          </Text>
        </Tooltip>
      ),
    },
    {
      id: "EXPORTED",
      title: (
        <Tooltip
          maxWidth={360}
          content="Here is the list of your Faire products that have already been transfered to your store. Please click on 'Delete from Wix' to remove these products from your store."
        >
          <Text>
            Exported
            {exportedData && exportedData !== 0 ? ` (${exportedData})` : ""}
          </Text>
        </Tooltip>
      ),
    },
    {
      id: "NOT_EXPORTED",
      title: (
        <Tooltip
          maxWidth={360}
          content="Here is the list of your Faire products that are not in your store. Please click on 'Import to Wix'  to send these products to your store."
        >
          <Text>
            Not Exported
            {notExportedData && notExportedData !== 0 ? ` (${notExportedData})` : ""}
          </Text>
        </Tooltip>
      ),
    },
  ];

  /*
  |--------------------------------------------------------------------------
  | Manage Side Panel
  |--------------------------------------------------------------------------
  |
  */
  const [showSidePanel, setShowSidePanel] = useState(false);

  const handleTabChange = (value: any) => {
    setCurrentPageNumber(1);
    setExportStatus(value.id);
  };

  const openPanel = (event: any) => setShowSidePanel(true); // Open Side Panel

  const closePanel = () => setShowSidePanel(false); // Close Side Panel

  /*  
  |--------------------------------------------------------------------------
  | Handle importing faire products
  |--------------------------------------------------------------------------
  |
  */
  const handleImportFaireProducts = () => {
    if (showConfirmationForImport) return;

    setShowConfirmationForImport(true);
  };

  /*
  |--------------------------------------------------------------------------
  | Handle confirmation
  |--------------------------------------------------------------------------
  |
  */
  const handleConfirmationForImport = (confirmationStatus: any) => {
    setShowConfirmationForImport(false);

    if (confirmationStatus) {
      importFaireProducts();
    }
  };

  const faireProductsQuery = useFaireProducts({
    page: currentPageNumber,
    perPage: itemsPerPage,
    keywords: keywords,
    status: status,
    categoryId: categoryId,
    exportStatus: exportStatus,
  });

  const faireProducts = addExpandProperties(faireProductsQuery.data?.data?.data);

  /*
  |--------------------------------------------------------------------------
  | Import faire products
  |--------------------------------------------------------------------------
  |
  */
  const { importFaireProducts, isImportingFaireProducts } = useFaireProductImport();

  /*
  |--------------------------------------------------------------------------
  | Handle page change
  |--------------------------------------------------------------------------
  |
  */
  const pageChangeHandler = (event: any) => {
    setCurrentPageNumber(event.page);
  };

  /*
  |--------------------------------------------------------------------------
  | Handle export
  |--------------------------------------------------------------------------
  |
  */
  const { setSelectedItem, selectedItem, setEmptySelectionItem, setItemSelection, selectedItemsCount } =
    useTableItemSelection({
      pageNumber: currentPageNumber,
      itemLists: faireProducts,
    });

  let productIds = mergeArrays(selectedItem).map((selectedProduct) => {
    return { product_id: selectedProduct };
  });

  const { BulkExportMutation, showExportConfirmation, setShowExportConfirmation } = useFaireProductBulkExport({
    faireProductsQuery: faireProductsQuery,
    setEmptySelectionItem: setEmptySelectionItem,
    productIds: productIds,
  });

  const handleExport = () => {
    if (exportInProgress) return;
    setExportInProgress(true);
    BulkExportMutation.mutate();
  };

  /*
  |--------------------------------------------------------------------------
  | Delete from wix
  |--------------------------------------------------------------------------
  |
  */
  const {
    deleteFromWix,
    setProductIdToBeDeletedFromWix,
    showDeleteFromWixConfirmation,
    setShowDeleteFromWixConfirmation,
  } = useWixProductsDelete({
    setExportedData,
    setNotExportedData,
    faireProductsQuery,
  });

  /*
  |--------------------------------------------------------------------------
  | Products table columns
  |--------------------------------------------------------------------------
  |
  */
  const columns = [
    {
      title: "",
      width: "8%",
      render: (row: any) => (
        <Layout>
          <Cell span={3}>
            <Box width="80px" height="80px" overflow="hidden">
              {row.thumbnail ? <img src={row.thumbnail} /> : <img src={picture} />}
            </Box>
          </Cell>
        </Layout>
      ),
    },
    {
      title: "Products",
      width: "22%",
      render: (row: any) => {
        const unpublishedStatus = row.lifecycle_state === "UNPUBLISHED" ? "neutral" : "general";
        const deletedStatus = row.lifecycle_state === "DELETED" ? "danger" : unpublishedStatus;
        const publishStatus = row.lifecycle_state === "PUBLISHED" ? "success" : deletedStatus;
        const draftStatus = row.lifecycle_state === "DRAFT" ? "standard" : publishStatus;
        return (
          <>
            <Tooltip content={row.name}>
              <Text weight="bold">{truncateText(row.name, 30)}</Text>
            </Tooltip>

            <Box margin="8px 0px 5px 0px">
              {row.sku ? <Text size="tiny">SKU:{row.sku}</Text> : <Text size="tiny">Total SKU:{row.total_sku}</Text>}
            </Box>

            <Badge size="tiny" skin={draftStatus}>
              {row.lifecycle_state}
            </Badge>
          </>
        );
      },
    },
    {
      title: "Price",
      width: "12%",
      render: (row: any) => {
        if (row.numberOfVariants > 0) {
          return (
            <>
              <Text size="small">Prices with variant/s</Text>
            </>
          );
        }
        return (
          <>
            <Text size="small">
              Wholesale {row.priceCurrency} {(row.minimumWholesalePrice / 100).toFixed(2)}
            </Text>
            <br />
            <Text size="small">
              Retail {row.priceCurrency} {(row.maximumRetailPrice / 100).toFixed(2)}
            </Text>
          </>
        );
      },
    },
    {
      title: "Categories",
      width: "18%",
      render: (row: any) => <>{row.taxonomies[0]?.name || "-"}</>,
    },
    {
      title: "Inventory",
      width: "10%",
      render: (row: any) => {
        return (
          <>
            {row.variants.some((variant: any) => variant.available_quantity != 0 && variant.track_quantity == 1) ? (
              <>
                {row.total_quantity ?? 0} in stock
                {row.numberOfVariants > 0
                  ? ` for ${row.numberOfVariants} variant${row.numberOfVariants > 1 ? "s" : ""}`
                  : ""}
              </>
            ) : (
              "Not Tracked"
            )}
          </>
        );
      },
    },
    {
      title: "View on",
      width: "10%",
      render: (row: any) => {
        let viewProductUrlCondition = row.exported_wix_product_id
          ? row.wix_details_url
          : `https://manage.wix.com/dashboard/${siteId}/store/products/product/${row.wix_product_id}`;

        return (
          <Layout>
            <Cell span={4}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "10px",
                  alignItems: "center",
                }}
              >
                {row.exported_wix_product_id || row.wix_product_id ? (
                  <div style={{ width: "50%" }}>
                    <Tooltip appendTo="window" content="View the product on dashboard">
                      <a href={viewProductUrlCondition} target="_blank">
                        <img width="32px" height="12px" src={WixLogo} />
                      </a>
                    </Tooltip>
                  </div>
                ) : (
                  <div style={{ width: "50%" }}> &nbsp;</div>
                )}
                <div style={{ width: "50%" }}>
                  <Tooltip appendTo="window" content="View the product on Faire dashboard">
                    <a href={row.details_url} target="_blank">
                      <img src={FiareLogo} />
                    </a>
                  </Tooltip>
                </div>
              </div>
            </Cell>
          </Layout>
        );
      },
    },
    {
      title: "Action",
      width: "28%",
      render: (row: any) => {
        const showImportButtonCondition =
          !row.is_exporting &&
          !row.exported_wix_product_id &&
          row.source === "faire" &&
          row.latest_export_error === null;

        return (
          <Layout>
            <Cell span={6}>
              {(row.exported_wix_product_id || row.exported) && row.latest_export_error === null && (
                <Button
                  size="tiny"
                  skin="destructive"
                  priority="secondary"
                  prefixIcon={<Icons.DeleteSmall />}
                  onClick={() => setProductIdToBeDeletedFromWix(row.exported_wix_product_id)}
                >
                  Delete from Store
                </Button>
              )}

              {row.is_exporting ? (
                <Tooltip content="This product is being imported to Wix.">
                  <Box align="center">
                    <Button style={{ backgroundColor: "#7A92A5" }} size="tiny" disabled>
                      <p>Importing</p>
                    </Button>
                  </Box>
                </Tooltip>
              ) : (
                ""
              )}

              {showImportButtonCondition && (
                <Button
                  size="tiny"
                  priority="secondary"
                  onClick={() => setSelectedProductId(row.product_id)}
                  prefixIcon={<Icons.DownloadImportSmall />}
                >
                  Import to Store
                </Button>
              )}

              {row.wix_product_id && (
                <Tooltip content="This product has been imported from wix">
                  <Box align="center">
                    <Button style={{ backgroundColor: "#7A92A5" }} size="tiny" disabled>
                      <p>Imported</p>
                    </Button>
                  </Box>
                </Tooltip>
              )}

              <Box width="100%" gap={"8px"} align="center" verticalAlign="middle" justifyItems="center">
                {row.latest_export_error !== null && !row.is_exporting && (
                  <Tooltip content={<>{row.latest_export_error}</>}>
                    <Icons.StatusWarning color="#eb847d" size="2rem" />
                  </Tooltip>
                )}
                {row.latest_export_error !== null && !row.is_exporting && (
                  <Tooltip content="Retry Exporting">
                    <Button size="tiny" priority="secondary" onClick={() => setSelectedProductId(row.product_id)}>
                      <Icons.Revert />
                      Retry
                    </Button>
                  </Tooltip>
                )}
              </Box>
            </Cell>

            {row.numberOfVariants > 0 && (
              <Cell span={6}>
                <IconButton
                  skin="standard"
                  priority="secondary"
                  size="tiny"
                  onClick={() => (row.expanded = !row.expanded)}
                >
                  {row.expanded ? <Icons.ChevronUp /> : <Icons.ChevronDown />}
                </IconButton>
              </Cell>
            )}
          </Layout>
        );
      },
    },
  ];

  /*
  |--------------------------------------------------------------------------
  | Actions toolbar
  |--------------------------------------------------------------------------
  |
  */
  const renderActionsToolbar = () => {
    return (
      <>
        <Divider direction="horizontal" />
        <TableToolbar>
          <TableToolbar.Item layout="button">
            <Button
              size="small"
              disabled={exportInProgress}
              prefixIcon={
                exportInProgress ? (
                  <span>
                    <Loader size="tiny" />
                  </span>
                ) : (
                  <Icons.DownloadImportSmall />
                )
              }
              onClick={() => setShowExportConfirmation(true)}
            >
              {exportInProgress ? (
                <Text skin="standard" weight="bold">
                  Importing {exportProgress}/{totalProducts} products
                </Text>
              ) : (
                `Import ${selectedItemsCount} product(s) to Wix`
              )}
            </Button>
          </TableToolbar.Item>
        </TableToolbar>
      </>
    );
  };

  // Render Product Variant Price
  const getVariantPrices = (product: any, prices: any) => {
    let priceElement = null;
    for (let index = 0; index < prices.length; index++) {
      const price = prices[index];
      if (product.defaultCurrency == price.wholesale_currency) {
        priceElement = (
          <div key={index}>
            <Text size="small">
              WholeSale {price.wholesale_currency} {(price.wholesale_price / 100).toFixed(2)}
            </Text>
            <br />
            <Text size="small">
              Retail {price.retail_currency} {(price.retail_price / 100).toFixed(2)}
            </Text>
          </div>
        );
      } else {
        priceElement = (
          <div key={index}>
            <Text size="small">
              WholeSale {price.wholesale_currency} {(price.wholesale_price / 100).toFixed(2)}
            </Text>
            <br />
            <Text size="small">
              Retail {price.retail_currency} {(price.retail_price / 100).toFixed(2)}
            </Text>
          </div>
        );
      }
    }
    return priceElement;
  };

  /*
  |--------------------------------------------------------------------------
  | Render Product details
  |--------------------------------------------------------------------------
  |
  */
  const renderProductDetails = (product: any) => {
    const detailsColumns = [
      {
        title: "Variant",
        render: (variant: any) => <Text size="small">{variant.name}</Text>,
      },
      {
        title: "SKU",
        render: (variant: any) => <Text size="small">{variant.sku}</Text>,
      },
      {
        title: "Price",
        render: (variant: any) => {
          return getVariantPrices(product, variant.variant_prices);
        },
      },
      {
        title: "Inventory",
        render: (variant: any) => {
          return variant.track_quantity == 0 && (variant.available_quantity == 0 || variant.available_quantity == null)
            ? "Not Tracked"
            : variant.available_quantity ?? 0;
        },
      },
    ];

    return (
      <Card showShadow hideOverflow>
        <Table skin="neutral" data={product.variants} columns={detailsColumns} isRowHighlight={() => true}>
          <Table.Content />
        </Table>
      </Card>
    );
  };

  /*
  |--------------------------------------------------------------------------
  | Render row details
  |--------------------------------------------------------------------------
  |
  */

  const renderRowDetails = (row: any) => {
    if (row.numberOfVariants > 0 && row.expandable) {
      return renderProductDetails(row);
    }
    return null;
  };

  useEffect(() => {
    faireCategoryQuery.refetch();
  }, [keywords, currentPageNumber, status, categoryId]);

  const debounced = useDebouncedCallback(
    function (value) {
      setKeywords(value);
      setCurrentPageNumber(1);
    },
    1000 // delay in ms
  );

  /*
  |--------------------------------------------------------------------------
  | Import wix products progress implementation
  |--------------------------------------------------------------------------
  |
  | An event will be provided with following data:
  | { progressed_percentage: 25 }. Based on the
  | data, a loader is to be rendered.
  |
  */
  const { setImportInProgress, importInProgress, setProgress, progress } = usePusherImportProgress({
    siteId: siteId,
    broadcastChannel: `${siteId}-import-faire-products`,
    broadcastAs: "importFaireProducts.progressed",
  });

  React.useEffect(() => {
    if (progress === 100) {
      setImportInProgress(false);
      setProgress(0);
      faireProductsQuery.refetch();
    }
  }, [progress]);

  /*
  |--------------------------------------------------------------------------
  | Bulk Export Progress Implementation 
  |--------------------------------------------------------------------------
  |
  */
  const {
    setExportInProgress,
    exportInProgress,
    setExportProgress,
    exportProgress,
    setBulkExportStatus,
    bulkExportStatus,
    totalProducts,
  } = usePusherBulkExportProgress({
    siteId: siteId,
    broadcastChannel: `${siteId}-bulk-export-faire-products`,
    broadcastAs: "bulkExportFaireProducts.progressed",
  });

  useEffect(() => {
    if (totalProducts == exportProgress && bulkExportStatus === "complete") {
      setExportInProgress(false);
      setExportProgress(0);
      faireProductsQuery.refetch();
      faireProductCountRefetch();
      setEmptySelectionItem();
      setBulkExportStatus("");
    }
  }, [bulkExportStatus]);

  /*
  |--------------------------------------------------------------------------
  | Product Export Progress Implementation
  |--------------------------------------------------------------------------
  |
  */
  const { exportProductInProgress, setExportProductInProgress, exportProductStatus } = usePusherExportProgress({
    siteId: siteId,
    broadcastChannel: `${siteId}-faire-product-export-progress`,
    broadcastAs: "faireProductExport.progress",
  });

  React.useEffect(() => {
    setExportProductInProgress(false);
    faireProductsQuery.refetch();

    if (exportProductStatus === "complete" && exportProductInProgress === false) {
      setExportedData((prevData: any) => prevData + 1);
      setNotExportedData((previousData: any) => previousData - 1);
    }
  }, [exportProductInProgress, exportProductStatus]);

  return (
    <WixDesignSystemProvider>
      <Page height="95vh">
        <Page.Header
          title={
            <Box gap={"10px"} align="space-between" verticalAlign="middle">
              Faire Products <SyncedDate source="faire" />
            </Box>
          }
          subtitle={
            <Text size="tiny">
              Here is a list of your Faire products. You can select the products you want to export to your WIX store.
              ‘Imported' badge be seen under the actions column for the products that have been imported from your WIX
              store to Faire. Please use the filters below to see products you have already exported or to see products
              based on status.
            </Text>
          }
          actionsBar={
            <Box direction="vertical" align="center" marginLeft={"20px"}>
              <Button
                onClick={handleImportFaireProducts}
                prefixIcon={
                  importInProgress ? (
                    <span className="import_progress_loader_margin">
                      <Loader size="tiny" />
                    </span>
                  ) : (
                    <Icons.Download />
                  )
                }
                disabled={importInProgress}
              >
                {importInProgress ? (
                  <Text skin="standard" weight="bold">
                    Importing products ... {progress}%
                  </Text>
                ) : !importInProgress && isImportingFaireProducts ? (
                  "Importing Products ..."
                ) : (
                  "Import Faire Products"
                )}
              </Button>
            </Box>
          }
        />
        <Page.Content>
          <TrialExpiryAlert />
          {/* Delete From Wix Confirmation Modal */}

          <Modal
            isOpen={showDeleteFromWixConfirmation}
            shouldCloseOnOverlayClick={true}
            onRequestClose={() => {
              setProductIdToBeDeletedFromWix("");
              setShowDeleteFromWixConfirmation(false);
            }}
          >
            <MessageModalLayout
              onCloseButtonClick={() => {
                setProductIdToBeDeletedFromWix("");
                setShowDeleteFromWixConfirmation(false);
              }}
              primaryButtonText="Yes"
              primaryButtonOnClick={() => {
                setShowDeleteFromWixConfirmation(false);
                deleteFromWix();
              }}
              secondaryButtonText="Cancel"
              secondaryButtonOnClick={() => {
                setProductIdToBeDeletedFromWix("");
                setShowDeleteFromWixConfirmation(false);
              }}
              title="Delete from Store"
              content={<Text>Are you sure you want to delete this product from Wix?</Text>}
            />
          </Modal>

          {/* Export Modal */}
          <ExportModal
            title={`Import - Faire -> Wix`}
            subtitle="All Selected products will be Exported to Wix Faire"
            source="faire"
            showExportConfirmation={showExportConfirmation}
            setShowExportConfirmation={() => {
              setShowExportConfirmation(false);
            }}
            handleExport={() => {
              setShowExportConfirmation(false);
              handleExport();
            }}
            selectedProducts={mergeArrays(selectedItem)}
          />

          {/* Upload To Wix Confirmation Modal */}
          <Modal
            isOpen={showConirmationForUploadToWix}
            shouldCloseOnOverlayClick={!uploadingToWix}
            onRequestClose={() => setShowConfirmationForUploadToWix(false)}
            onAfterClose={() => handleCancelUploadToWix()}
          >
            <MessageModalLayout
              primaryButtonProps={{ disabled: uploadingToWix }}
              secondaryButtonProps={{ disabled: uploadingToWix }}
              onCloseButtonClick={
                !uploadingToWix
                  ? () => {
                      handleCancelUploadToWix();
                      setShowConfirmationForUploadToWix(false);
                    }
                  : undefined
              }
              primaryButtonText={uploadingToWix ? "Import in progress" : "Yes"}
              primaryButtonOnClick={() => {
                handleConfirmUploadToWix();
              }}
              secondaryButtonText="Cancel"
              secondaryButtonOnClick={() => {
                handleCancelUploadToWix();
                setShowConfirmationForUploadToWix(false);
              }}
              title="Import to Wix"
              content={<Text>Are you sure you want to import this product to Wix?</Text>}
            />
          </Modal>

          {/* Import Faire Products Confirmation Modal */}
          <Modal isOpen={showConfirmationForImport} onRequestClose={handleConfirmationForImport}>
            <MessageModalLayout
              onCloseButtonClick={() => handleConfirmationForImport(false)}
              primaryButtonText="Yes"
              primaryButtonOnClick={() => handleConfirmationForImport(true)}
              secondaryButtonText="Cancel"
              secondaryButtonOnClick={() => handleConfirmationForImport(false)}
              title="Import Faire Products"
              content={<Text>Are you sure you want to import Faire products?</Text>}
            />
          </Modal>

          {/* Table  */}
          <Card className={"faire_product_table"}>
            <Table
              data={faireProducts}
              columns={columns}
              rowDetails={(row) => renderRowDetails(row)}
              isRowHighlight={(rowData) => rowData?.highlight}
              selectionDisabled={(row) =>
                (row.wix_product_id || row.exported_wix_product_id || row.is_exporting) && true
              }
              showSelection
              onSelectionChanged={setItemSelection}
              selectedIds={selectedItem[currentPageNumber]}
            >
              <Page.Sticky>
                <Card>
                  <TableToolbar removeVerticalPadding>
                    <TableToolbar.ItemGroup position="start">
                      <Tabs
                        size="medium"
                        hasDivider={false}
                        items={exportStatusOptions}
                        activeId={exportStatus}
                        onClick={(value) => handleTabChange(value)}
                      />
                    </TableToolbar.ItemGroup>
                    <TableToolbar.ItemGroup position="end">
                      <TableToolbar.Item className="">
                        <Box direction="horizontal" gap={"SP2"}>
                          {(categoryId !== " " || status !== " ") && (
                            <Button
                              size="small"
                              priority="secondary"
                              prefixIcon={<Icons.Dismiss />}
                              onClick={() => {
                                setCategoryId(" ");
                                setStatus(" ");
                                setCategoryKeywords("");
                              }}
                            >
                              Clear
                            </Button>
                          )}

                          <Button
                            size="small"
                            priority="secondary"
                            prefixIcon={<Icons.ContentFilterSmall />}
                            onClick={openPanel}
                          >
                            Filter
                          </Button>
                        </Box>
                      </TableToolbar.Item>

                      <TableToolbar.Item>
                        <Search
                          placeholder="Product name/SKU"
                          size="small"
                          onChange={(e) => debounced(e.target.value)}
                          onClear={() => setKeywords("")}
                          value={keywords}
                        />
                      </TableToolbar.Item>
                    </TableToolbar.ItemGroup>
                  </TableToolbar>
                  <Table.ToolbarContainer>
                    {() => (selectedItemsCount > 0 || exportInProgress ? renderActionsToolbar() : "")}
                  </Table.ToolbarContainer>
                  <Table.Titlebar />
                </Card>
              </Page.Sticky>

              {faireProductsQuery.isLoading || faireProductsQuery.isFetching ? (
                <Loading title="Fetching Faire Products" />
              ) : (
                <>
                  {!faireProductsQuery.isLoading && faireProducts?.length ? (
                    <Table.Content titleBarVisible={false} />
                  ) : (
                    <EmptyTable
                      hasKeywords={keywords.length > 0}
                      onChange={() => setKeywords("")}
                      title="No products found"
                      description="Couldn't find any products. Try adding new products or refine your filter."
                      actions={[
                        <Button
                          priority="secondary"
                          key={1}
                          size="small"
                          prefixIcon={<Icons.Add />}
                          onClick={handleImportFaireProducts}
                        >
                          Import products
                        </Button>,
                      ]}
                    />
                  )}
                </>
              )}
            </Table>
          </Card>
          {faireProductsQuery.isSuccess && (
            <Layout justifyItems="center">
              <Cell>
                <Box margin="SP4 0 0 0">
                  <Pagination
                    currentPage={currentPageNumber}
                    totalPages={faireProductsQuery.data?.data?.last_page}
                    onChange={pageChangeHandler}
                  />
                </Box>
              </Cell>
            </Layout>
          )}
        </Page.Content>
      </Page>

      {/* Side Panel Modal */}
      <SidePanelModal
        closePanel={closePanel}
        showSidePanel={showSidePanel}
        setShowSidePanel={setShowSidePanel}
        items={[
          accordionItemBuilder({
            title: "All Statuses",
            children: (
              <Box direction="vertical" width="100%">
                <Dropdown
                  size="small"
                  border="round"
                  options={faireStatusOptions}
                  selectedId={status}
                  onSelect={(status) => {
                    setStatus(status.id);
                    setCurrentPageNumber(1);
                  }}
                />
              </Box>
            ),
          }),
          accordionItemBuilder({
            title: "Categories",
            children: (
              <Box width="100%" direction="vertical">
                <AutoComplete
                  size="small"
                  border="round"
                  focusOnSelectedOption
                  selectedHighlight
                  selectedId={categoryId}
                  onSelect={(category: any) => {
                    setCategoryId(category.id);
                    setCurrentPageNumber(1);
                    setCategoryKeywords(category.value);
                  }}
                  placeholder={"All Categories"}
                  infiniteScroll
                  hasMore={hasMore}
                  loadMore={fetchCategories}
                  options={faireCategoryOptions}
                  onChange={(event: any) => {
                    setCategoryKeywords(event.target.value);
                    debouncedCategory(event.target.value);
                  }}
                  value={categoryKeywords}
                />
              </Box>
            ),
          }),
        ]}
      />
    </WixDesignSystemProvider>
  );
};

export default withProviders(Index);
